import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { GuideStep } from "@/hooks/usePageGuide";

interface PageGuideProps {
  isActive: boolean;
  step: GuideStep | null;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
}

export default function PageGuide({
  isActive,
  step,
  currentStep,
  totalSteps,
  onNext,
  onPrev,
  onClose,
}: PageGuideProps) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isActive || !step) {
      setRect(null);
      return;
    }
    const update = () => {
      const el = document.querySelector(step.selector);
      if (el) setRect(el.getBoundingClientRect());
      else setRect(null);
    };
    // Small delay to let DOM settle
    const timer = setTimeout(update, 150);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [isActive, step]);

  if (!isActive || !step) return null;

  const padding = 8;
  const pos = step.position || "bottom";

  // Calculate tooltip position
  const getTooltipStyle = (): React.CSSProperties => {
    if (!rect) return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    const gap = 12;
    switch (pos) {
      case "top":
        return { bottom: `${window.innerHeight - rect.top + gap}px`, left: `${rect.left + rect.width / 2}px`, transform: "translateX(-50%)" };
      case "left":
        return { top: `${rect.top + rect.height / 2}px`, right: `${window.innerWidth - rect.left + gap}px`, transform: "translateY(-50%)" };
      case "right":
        return { top: `${rect.top + rect.height / 2}px`, left: `${rect.right + gap}px`, transform: "translateY(-50%)" };
      case "bottom":
      default:
        return { top: `${rect.bottom + gap}px`, left: `${rect.left + rect.width / 2}px`, transform: "translateX(-50%)" };
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999]" dir="rtl">
        {/* Overlay with cutout */}
        <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: "none" }}>
          <defs>
            <mask id="guide-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {rect && (
                <rect
                  x={rect.left - padding}
                  y={rect.top - padding}
                  width={rect.width + padding * 2}
                  height={rect.height + padding * 2}
                  rx="8"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.5)"
            mask="url(#guide-mask)"
            style={{ pointerEvents: "auto" }}
            onClick={onClose}
          />
        </svg>

        {/* Highlighted element border */}
        {rect && (
          <motion.div
            layoutId="guide-highlight"
            className="absolute border-2 border-primary rounded-lg pointer-events-none"
            style={{
              top: rect.top - padding,
              left: rect.left - padding,
              width: rect.width + padding * 2,
              height: rect.height + padding * 2,
            }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          />
        )}

        {/* Tooltip card */}
        <motion.div
          ref={tooltipRef}
          className="fixed bg-card border border-border rounded-xl shadow-xl p-4 max-w-xs w-80 z-[10000]"
          style={getTooltipStyle()}
          initial={{ opacity: 0, y: pos === "top" ? 10 : -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          key={currentStep}
          transition={{ duration: 0.25 }}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-2 left-2 p-1 rounded-full hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>

          {/* Content */}
          <div className="mb-3">
            <h4 className="font-bold text-foreground text-sm mb-1">{step.title}</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {currentStep + 1} / {totalSteps}
            </span>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={onPrev}
                disabled={currentStep === 0}
                className="h-7 px-2"
              >
                <ChevronRight className="w-3 h-3" />
              </Button>
              <Button
                size="sm"
                onClick={onNext}
                className="h-7 px-3 text-xs"
              >
                {currentStep === totalSteps - 1 ? "إنهاء" : "التالي"}
                {currentStep < totalSteps - 1 && <ChevronLeft className="w-3 h-3 mr-1" />}
              </Button>
            </div>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-1 mt-2">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === currentStep ? "bg-primary" : "bg-muted-foreground/30"
                }`}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
